import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import api from "../utils/axios";
import { LoadingScreen } from "../components/LoadingScreen";

export interface User {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  emailVerified: boolean;
  requiredActions: string[];
}

interface RequiredActionMessages {
  required_actions: string[];
  messages: Record<string, string>;
  has_required_actions: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string, turnstileToken?: string) => Promise<User>;
  logout: () => Promise<void>;
  token: string | null;
  refreshToken: string | null;
  refreshAccessToken: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  getRequiredActionMessages: () => Promise<RequiredActionMessages | null>;
  activeRole: 'user' | 'admin';
  switchRole: (role: 'user' | 'admin') => void;
  hasAdminRole: boolean;
  isAdminOnly: boolean;
  /** Guest accounts only see projects they were invited to; account-type UI gates on this. */
  isGuest: boolean;
}

// Exported so a component can read it without `useAuth`, which throws when the
// provider is absent. A gate that renders in twenty places must degrade to
// "not an admin" rather than crash the tree it is protecting.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(
    localStorage.getItem("refreshToken")
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState<'user' | 'admin'>(
    (localStorage.getItem("activeRole") as 'user' | 'admin') || 'user'
  );
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  // Effect to set up proactive token refresh
  useEffect(() => {
    if (!isAuthenticated || !tokenExpiresAt) {
      return;
    }

    // Calculate when to refresh: 5 minutes before expiration
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
    const now = Date.now();
    const timeUntilRefresh = tokenExpiresAt - now - refreshBuffer;

    console.log(`⏰ Token expires at ${new Date(tokenExpiresAt).toLocaleString()}`);
    console.log(`⏰ Will refresh token in ${Math.round(timeUntilRefresh / 1000)} seconds`);

    if (timeUntilRefresh <= 0) {
      // Token is about to expire or already expired, refresh immediately
      console.log('⚠️ Token is about to expire, refreshing immediately...');
      refreshAccessToken().catch((error) => {
        console.error('❌ Immediate token refresh failed:', error);
      });
      return;
    }

    // Set up timer to refresh token before it expires
    const refreshTimer = setTimeout(() => {
      console.log('⏰ Proactive token refresh triggered');
      refreshAccessToken().catch((error) => {
        console.error('❌ Proactive token refresh failed:', error);
      });
    }, timeUntilRefresh);

    return () => {
      clearTimeout(refreshTimer);
    };
  }, [isAuthenticated, tokenExpiresAt]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const verifyToken = async () => {
        try {
          console.log('🔄 Verifying token and fetching fresh user data on app initialization...');
          const response = await api.get("/auth/me?fresh=true");
          console.log('📥 Received fresh user data on initialization:', response.data);
          setUser(response.data);
          setIsAuthenticated(true);
          
          // Decode token to get expiration time
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              if (payload.exp) {
                const expiresAt = payload.exp * 1000; // Convert to milliseconds
                setTokenExpiresAt(expiresAt);
                console.log(`✅ Token expires at: ${new Date(expiresAt).toLocaleString()}`);
              }
            }
          } catch (decodeError) {
            console.warn('⚠️ Could not decode token expiration:', decodeError);
          }
          
          // If user has only admin role and activeRole is not set to admin, update it
          const userData = response.data;
          const userHasAdminRole = userData?.roles?.includes('admin') ?? false;
          const userIsAdminOnly = userHasAdminRole && userData?.roles?.length === 1;
          const storedActiveRole = localStorage.getItem("activeRole") as 'user' | 'admin';
          
          if (userIsAdminOnly && storedActiveRole !== 'admin') {
            setActiveRole('admin');
            localStorage.setItem("activeRole", 'admin');
            console.log('✅ Admin-only user detected, setting activeRole to admin');
          }
          
          console.log('✅ Authentication initialized with fresh user data');
        } catch (error) {
          console.error('❌ Token verification failed:', error);
          logout();
        } finally {
          setLoading(false);
        }
      };
      verifyToken();
    } else {
      setLoading(false);
    }
    // Axios interceptor to always attach token
    const interceptor = api.interceptors.request.use(
      (config) => {
        const storedToken = localStorage.getItem("token");
        if (storedToken) {
          config.headers["Authorization"] = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    return () => {
      api.interceptors.request.eject(interceptor);
    };
  }, [token]);

  const refreshAccessToken = async () => {
    try {
      const storedRefreshToken = localStorage.getItem("refreshToken");
      if (!storedRefreshToken) {
        throw new Error("No refresh token available");
      }

      console.log('🔄 Refreshing access token...');
      const response = await api.post("/auth/refresh", {
        refresh_token: storedRefreshToken,
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;

      setToken(access_token);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
        localStorage.setItem("refreshToken", newRefreshToken);
      }

      localStorage.setItem("token", access_token);
      api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      // Decode new token to get expiration time
      try {
        const tokenParts = access_token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.exp) {
            const expiresAt = payload.exp * 1000; // Convert to milliseconds
            setTokenExpiresAt(expiresAt);
            console.log(`✅ Token refreshed successfully. New expiration: ${new Date(expiresAt).toLocaleString()}`);
          }
        }
      } catch (decodeError) {
        console.warn('⚠️ Could not decode refreshed token expiration:', decodeError);
      }
    } catch (error) {
      console.error("❌ Token refresh failed:", error);
      logout();
      throw error;
    }
  };

  const refreshUserData = async () => {
    console.log('🔄 refreshUserData called, token exists:', !!token);
    try {
      if (token) {
        console.log('📡 Fetching fresh user data from /auth/me?fresh=true...');
        const response = await api.get("/auth/me?fresh=true");
        console.log('📥 Received fresh user data from API:', response.data);
        console.log('👤 Previous user data:', user);
        setUser(response.data);
        console.log('✅ User data updated in context with fresh data');
      } else {
        console.log('❌ No token available, skipping user data refresh');
      }
    } catch (error) {
      console.error("❌ Failed to refresh user data:", error);
      // Don't logout on user data refresh failure, just log the error
    }
  };

  const login = async (username: string, password: string, turnstileToken?: string) => {
    try {
      const payload: Record<string, string> = { username, password };
      if (turnstileToken) {
        payload.turnstile_token = turnstileToken;
      }
      const response = await api.post("/auth/login", payload);

      const { access_token, refresh_token, user: userData } = response.data;

      setToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Decode token to get expiration time
      try {
        const tokenParts = access_token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.exp) {
            const expiresAt = payload.exp * 1000; // Convert to milliseconds
            setTokenExpiresAt(expiresAt);
            console.log(`✅ Logged in successfully. Token expires at: ${new Date(expiresAt).toLocaleString()}`);
          }
        }
      } catch (decodeError) {
        console.warn('⚠️ Could not decode token expiration:', decodeError);
      }
      
      // If user has ONLY admin role, set activeRole to 'admin'
      // Otherwise, default to 'user' role on login
      const userHasAdminRole = userData?.roles?.includes('admin') ?? false;
      const userIsAdminOnly = userHasAdminRole && userData?.roles?.length === 1;
      const defaultRole = userIsAdminOnly ? 'admin' : 'user';
      setActiveRole(defaultRole);
      localStorage.setItem("activeRole", defaultRole);

      localStorage.setItem("token", access_token);
      if (refresh_token) {
        localStorage.setItem("refreshToken", refresh_token);
      }
      api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      // Return user data so it can be used immediately after login
      return userData;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const storedRefreshToken = localStorage.getItem("refreshToken");
      if (storedRefreshToken) {
        // Call logout endpoint to invalidate tokens on server
        await api.post("/auth/logout", { refresh_token: storedRefreshToken });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setActiveRole('user');
      setTokenExpiresAt(null);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("activeRole");
      delete api.defaults.headers.common["Authorization"];
    }
  };

  const getRequiredActionMessages =
    async (): Promise<RequiredActionMessages | null> => {
      try {
        if (!isAuthenticated || !token) {
          return null;
        }

        const response = await api.get("/auth/required-actions");
        return response.data;
      } catch (error) {
        console.error("Error getting required action messages:", error);
        return null;
      }
    };

  const switchRole = (role: 'user' | 'admin') => {
    // Only allow switching to admin if user has admin role
    if (role === 'admin' && !user?.roles?.includes('admin')) {
      console.warn('User does not have admin role');
      return;
    }
    // Don't allow switching to user if user has only admin role
    if (role === 'user' && user?.roles?.includes('admin') && user?.roles?.length === 1) {
      console.warn('Admin-only users cannot switch to user view');
      return;
    }
    setActiveRole(role);
    localStorage.setItem("activeRole", role);
  };

  const hasAdminRole = user?.roles?.includes('admin') ?? false;
  // Check if user has ONLY admin role (not both user and admin)
  const isAdminOnly = hasAdminRole && user?.roles?.length === 1;
  const isGuest = user?.roles?.includes('guest') ?? false;

  const value: AuthContextType = {
    user,
    isAuthenticated,
    login,
    logout,
    token,
    refreshToken,
    refreshAccessToken,
    refreshUserData,
    getRequiredActionMessages,
    activeRole,
    switchRole,
    hasAdminRole,
    isAdminOnly,
    isGuest,
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};